/**
 * Kiosk-side poller for remote fiscal-printer commands.
 *
 * The fiscal printer hangs off this machine's USB, so the server can never
 * reach it. The admin presses a button, the server raises a per-place "pending"
 * flag, and this poller — running only on the kiosk — notices it, runs the
 * command on the local bridge and reports back so the flag is cleared.
 *
 * Mirrors datecsEndOfDay.js, which does the same for the card terminal.
 */

import { API_URL, DATECS_BRIDGE_URL } from '../../constants';
import Auth from '../../utils/Auth';

const POLL_MS = 60 * 1000; // once a minute, like the End-of-Day poller
let running = false; // never let two commands overlap on the serial line
let timer = null;

// What each admin command turns into on the bridge.
const BRIDGE_CALLS = {
  xreport: () => ({ path: '/fiscal/report', body: { reset: false } }),
  // A Z report closes the day and cannot be undone — the admin side is what
  // confirms it; by the time it reaches here the decision is made.
  zreport: () => ({ path: '/fiscal/report', body: { reset: true } }),
  duplicate: () => ({ path: '/fiscal/duplicate', body: {} }),
  // No number = the last receipt; the bridge resolves it from the device.
  copy: (p) => ({ path: '/fiscal/copy', body: p.docNumber ? { docNumber: p.docNumber } : {} }),
  // Money in and out of the drawer (command 70). Not a convenience: a storno
  // is paid IN CASH, and the device refuses to pay out money it does not
  // hold — so a card-only kiosk cannot issue a single refund until somebody
  // has deposited a float. `read` only counts the drawer and prints nothing.
  cash: (p) => ({
    path: '/fiscal/cash',
    body: p.read ? { read: true } : { amount: p.amount },
  }),
  // Periodic reports out of the fiscal memory. With no `to`, the device reads a
  // 4-digit `from` as a whole month and a 2-digit one as a whole year.
  period: (p) => ({ path: '/fiscal/period-report', body: { from: p.from, to: p.to } }),
  periodfull: (p) => ({
    path: '/fiscal/period-report',
    body: { from: p.from, to: p.to, detailed: true },
  }),
  // Storno (сторно). Without `items` the device reverses the whole
  // original out of its own journal; with them it reverses only those lines and
  // needs the original's УНП, time and FM number, which the server stored
  // when the sale was printed.
  storno: (p) => ({
    path: '/fiscal/storno',
    body: {
      docNumber: p.docNumber,
      reason: p.reason,
      reasonText: p.reasonText,
      items: p.items,
      original: p.original,
      // No payment mode: the device refuses a reversal paid by card even
      // with a full drawer, so the bridge always pays one in cash.
      // Впиши липсващото в касата, вместо да откажеш. Само ако админът
      // го е поискал — значи реални пари в чекмеджето.
      topUpCash: !!p.topUpCash,
    },
  }),
};

function currentPlaceId() {
  try {
    return Auth.getOpenedPlace ? Auth.getOpenedPlace() : null;
  } catch (e) {
    return null;
  }
}

async function tick() {
  if (running) return;
  const placeId = currentPlaceId();
  if (!placeId) return;

  let pending;
  try {
    const r = await fetch(
      API_URL + 'kiosk/fiscal-command/pending?place_id=' + encodeURIComponent(placeId)
    );
    pending = await r.json();
  } catch (e) {
    return; // server unreachable — try again next tick
  }
  if (!pending || !pending.pending || !BRIDGE_CALLS[pending.command]) return;

  running = true;
  // The whole pending payload goes in: a partial storno carries lines, a
  // period report carries dates, and the simple commands ignore the rest.
  const { path, body } = BRIDGE_CALLS[pending.command](pending);
  let result = null;
  let ok = false;
  let error = null;

  try {
    const r = await fetch(DATECS_BRIDGE_URL + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    result = await r.json().catch(() => null);
    ok = r.ok && !(result && result.ok === false);
    if (!ok) error = (result && result.error) || 'принтерът отказа командата';
  } catch (e) {
    // Bridge down or printer unplugged: leave the flag so a later tick retries
    // once it is back, exactly like the End-of-Day poller does.
    running = false;
    return;
  }

  try {
    await fetch(API_URL + 'kiosk/fiscal-command/complete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ placeId, command: pending.command, ok, result, error }),
    });
  } catch (e) {
    /* if this fails the flag stays and a later tick clears it */
  }

  running = false;
}

/**
 * Start polling for pending fiscal commands. No-op unless in kiosk mode.
 * @returns {() => void} stop function
 */
export function startFiscalCommandPoller() {
  if (timer) return () => stopFiscalCommandPoller();
  if (!Auth.isKioskMode || !Auth.isKioskMode()) return () => {};

  const run = () => { tick().catch(() => {}); };
  timer = setInterval(run, POLL_MS);
  run(); // also check right away
  return () => stopFiscalCommandPoller();
}

export function stopFiscalCommandPoller() {
  if (timer) {
    clearInterval(timer);
    timer = null;
  }
}
